/**
 * 语料选择弹窗
 * v9.2 新增：用于在场景卡片上添加语料
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Badge } from '@/shared/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  Search,
  Loader2,
  FileText,
  Plus,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import { FacetApiService } from '@/modules/identity/services'
import type { Corpus, CorpusListParams } from '@/modules/identity/types'
import type { SceneTag } from '@/modules/identity/types/facet'
import { SCENE_TAG_CONFIG } from '@/modules/identity/types/facet'
import { CORPUS_TYPE_CONFIG } from '@/modules/identity/types'

interface CorpusSelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  identityId: string
  sceneTag: SceneTag
  /** 已经关联到该场景的语料 ID 列表 */
  existingCorpusIds?: string[]
  onSuccess?: () => void
}

export function CorpusSelectDialog({
  open,
  onOpenChange,
  identityId,
  sceneTag,
  existingCorpusIds = [],
  onSuccess,
}: CorpusSelectDialogProps) {
  const { showSuccess, showError } = useToast()
  
  // 列表状态
  const [corpusList, setCorpusList] = useState<Corpus[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  
  // 选择状态
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  
  // 提交状态
  const [submitting, setSubmitting] = useState(false)

  // 场景配置
  const sceneConfig = SCENE_TAG_CONFIG[sceneTag]

  // 加载语料列表
  const loadCorpus = useCallback(async () => {
    setLoading(true)
    try {
      const params: CorpusListParams = {
        page: 1,
        page_size: 100, // 一次加载较多
      }
      if (searchText) {
        params.search = searchText
      }

      const response = await IdentityApiService.listCorpus(identityId, params)
      // 防御性编程：后端可能返回 items 或 data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      const corpusData = responseData?.data || responseData?.items || []
      setCorpusList(corpusData)
    } catch (error) {
      console.error('Failed to load corpus:', error)
      showError('无法加载语料列表', '加载失败')
    } finally {
      setLoading(false)
    }
  }, [identityId, searchText, showError])

  useEffect(() => {
    if (open) {
      loadCorpus()
      setSelectedIds(new Set())
    }
  }, [open, loadCorpus])

  // 选择操作
  const handleSelectOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  // 全选/取消全选（仅选择未关联的）
  const availableCorpus = corpusList.filter(c => !existingCorpusIds.includes(c.id))
  const allSelected = availableCorpus.length > 0 && availableCorpus.every(c => selectedIds.has(c.id))
  
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(availableCorpus.map(c => c.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [availableCorpus])

  // 提交关联
  const handleSubmit = useCallback(async () => {
    if (selectedIds.size === 0) {
      showError('请选择要添加的语料', '操作提示')
      return
    }

    setSubmitting(true)
    try {
      await FacetApiService.associateCorpus(identityId, sceneTag, {
        corpus_ids: Array.from(selectedIds),
      })
      showSuccess(`已添加 ${selectedIds.size} 份语料到「${sceneConfig.label}」场景`, '添加成功')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to associate corpus:', error)
      showError('添加失败，请稍后重试', '操作失败')
    } finally {
      setSubmitting(false)
    }
  }, [identityId, sceneTag, selectedIds, sceneConfig.label, showSuccess, showError, onOpenChange, onSuccess])

  // 计算已选择的条数
  const selectedItemCount = corpusList
    .filter(c => selectedIds.has(c.id))
    .reduce((sum, c) => sum + (c.item_count || 1), 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            添加语料到「{sceneConfig.icon} {sceneConfig.label}」场景
          </DialogTitle>
          <DialogDescription>
            从语料库中选择要添加到此场景的语料，一份语料可以关联到多个场景
          </DialogDescription>
        </DialogHeader>

        {/* 搜索框 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="搜索语料名称或内容..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* 语料列表 */}
        <div className="flex-1 overflow-auto border rounded-lg min-h-[300px]">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-violet-600" />
              <p className="text-slate-500">加载中...</p>
            </div>
          ) : corpusList.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">暂无语料</p>
              <p className="text-sm">请先在「语料管理」中上传语料</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>语料名称</TableHead>
                  <TableHead className="w-20">类型</TableHead>
                  <TableHead className="w-20">条数</TableHead>
                  <TableHead className="w-32">已关联场景</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {corpusList.map((corpus) => {
                  const isExisting = existingCorpusIds.includes(corpus.id)
                  const isSelected = selectedIds.has(corpus.id)
                  
                  return (
                    <TableRow 
                      key={corpus.id}
                      className={isExisting ? 'opacity-50 bg-slate-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected || isExisting}
                          disabled={isExisting}
                          onCheckedChange={(checked) => handleSelectOne(corpus.id, !!checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">
                            {corpus.name || `${corpus.type}_${corpus.created_at?.slice(0, 10)}`}
                          </p>
                          <p className="text-xs text-slate-400 line-clamp-1">
                            {corpus.content?.slice(0, 50)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {CORPUS_TYPE_CONFIG[corpus.type]?.icon} {CORPUS_TYPE_CONFIG[corpus.type]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {corpus.item_count || '-'}
                      </TableCell>
                      <TableCell>
                        {corpus.scenes && corpus.scenes.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {corpus.scenes.map(scene => (
                              <span key={scene} className="text-sm" title={SCENE_TAG_CONFIG[scene]?.label}>
                                {SCENE_TAG_CONFIG[scene]?.icon}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 底部信息 */}
        {selectedIds.size > 0 && (
          <div className="text-sm text-violet-600 bg-violet-50 px-3 py-2 rounded-lg">
            已选择 {selectedIds.size} 份语料（约 {selectedItemCount} 条内容）
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || selectedIds.size === 0}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            添加到「{sceneConfig.label}」
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
